import React from "react";
import { Card, Typography, Space, Tag, Button, Popconfirm, Empty } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LaptopOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import usePersonnelInfoService from "../../../../services/usePersonnelInfoService";

const { Text } = Typography;
const EmptyState = ({ competencyName }) => (
  <Empty
    description={`Henüz ${competencyName} bilgisi bulunmuyor`}
    image={Empty.PRESENTED_IMAGE_SIMPLE}
  />
);

export const LanguageCard = ({ data, onEdit }) => {
  const { useDeletePersonnelForeignLanguage } = usePersonnelInfoService();
  const { mutate: deletePersonnelForeignLanguage } =
    useDeletePersonnelForeignLanguage();
  return (
    <Card
      title={
        <Space>
          <GlobalOutlined />
          <Text>Yabancı Diller</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {data.length === 0 ? (
        <EmptyState competencyName="yabancı dil bilgisi" />
      ) : (
        data.map((item) => (
          <Card
            key={item.id}
            type="inner"
            style={{ marginBottom: 8 }}
            title={item.foreignLanguage?.name}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEdit(item)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Yabancı Dil Bilgisini Sil"
                  description="Bu yabancı dil bilgisini silmek istediğinize emin misiniz?"
                  onConfirm={() => deletePersonnelForeignLanguage(item.id)}
                  okText="Evet"
                  cancelText="Hayır">
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            }>
            <Space direction="vertical">
              <Text>
                Öğrenilen Yer: {item.foreignLanguageLearnedPlatform.description}
              </Text>
              <Space>
                <Tag color="blue">Okuma: {item.readingLevel.description}</Tag>
                <Tag color="green">Yazma: {item.writingLevel.description}</Tag>
                <Tag color="orange">
                  Konuşma: {item.speakingLevel.description}
                </Tag>
              </Space>
            </Space>
          </Card>
        ))
      )}
    </Card>
  );
};

export const ComputerSkillCard = ({ data, onEdit }) => {
  const { useDeletePersonnelComputerInformation } = usePersonnelInfoService();
  const { mutate: deletePersonnelComputerInformation } =
    useDeletePersonnelComputerInformation();
  return (
    <Card
      title={
        <Space>
          <LaptopOutlined />
          <Text>Bilgisayar Bilgileri</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {data.length === 0 ? (
        <EmptyState competencyName="Bilgisayar Yetkinliği" />
      ) : (
        data.map((item) => (
          <Card
            key={item.id}
            type="inner"
            style={{ marginBottom: 8 }}
            title={item.computerInformation?.name}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEdit(item)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Bilgisayar Bilgisini Sil"
                  description="Bu bilgisayar bilgisini silmek istediğinize emin misiniz?"
                  onConfirm={() => deletePersonnelComputerInformation(item.id)}
                  okText="Evet"
                  cancelText="Hayır">
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            }>
            <Space direction="vertical">
              <Text>Tecrübe: {item.yearsOfExperience.description}</Text>
              <Text>Seviye: {item.level.description}</Text>
              {item.computerInformation.description && (
                <Text>Açıklama: {item.description}</Text>
              )}
            </Space>
          </Card>
        ))
      )}
    </Card>
  );
};

export const CourseSeminarCard = ({ data, onEdit }) => {
  const { useDeletePersonnelCourse } = usePersonnelInfoService();
  const { mutate: deletePersonnelCourse } = useDeletePersonnelCourse();
  return (
    <Card
      title={
        <Space>
          <ReadOutlined />
          <Text>Kurs/Seminer Bilgisi</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {data.length === 0 ? (
        <EmptyState competencyName="Kurs/Seminer Bilgisi" />
      ) : (
        data.map((item) => (
          <Card
            key={item.id}
            type="inner"
            style={{ marginBottom: 8 }}
            title={item.courseName}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEdit(item)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Kurs/Seminer Bilgisini Sil"
                  description="Bu kurs/seminer bilgisini silmek istediğinize emin misiniz?"
                  onConfirm={() => deletePersonnelCourse(item.id)}
                  okText="Evet"
                  cancelText="Hayır">
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            }>
            <Space direction="vertical">
              <Text>Kurum: {item.institutionName}</Text>
              <Text>
                Tarih: {item.month}/{item.year}
              </Text>
              {item.description && <Text>Açıklama: {item.description}</Text>}
            </Space>
          </Card>
        ))
      )}
    </Card>
  );
};

export const CertificateCard = ({ data, onEdit }) => {
  const { useDeletePersonnelCertificate } = usePersonnelInfoService();
  const { mutate: deletePersonnelCertificate } =
    useDeletePersonnelCertificate();
  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined />
          <Text>Sertifika Bilgisi</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {data.length === 0 ? (
        <EmptyState competencyName="Sertifika Bilgisi" />
      ) : (
        data.map((item) => (
          <Card
            key={item.id}
            type="inner"
            style={{ marginBottom: 8 }}
            title={item.certificateName}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEdit(item)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Sertifika Bilgisini Sil"
                  description="Bu sertifika bilgisini silmek istediğinize emin misiniz?"
                  onConfirm={() => deletePersonnelCertificate(item.id)}
                  okText="Evet"
                  cancelText="Hayır">
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            }>
            <Space direction="vertical">
              <Text>Sertifika No: {item.certificateNumber}</Text>
              <Text>Veren Kurum: {item.institutionName}</Text>
              <Text>
                Tarih: {item.month}/{item.year}
              </Text>
              {item.description && <Text>Açıklama: {item.description}</Text>}
            </Space>
          </Card>
        ))
      )}
    </Card>
  );
};

export const ExamCard = ({ data, onEdit }) => {
  const { useDeletePersonnelExam } = usePersonnelInfoService();
  const { mutate: deletePersonnelExam } = useDeletePersonnelExam();
  return (
    <Card
      title={
        <Space>
          <GlobalOutlined />
          <Text>Sınav Bilgisi</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}>
      {data.length === 0 ? (
        <EmptyState competencyName="Sınav Bilgisi" />
      ) : (
        data.map((item) => (
          <Card
            key={item.id}
            type="inner"
            style={{ marginBottom: 8 }}
            title={item.examName}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => onEdit(item)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Sınav Bilgisini Sil"
                  description="Bu sınav bilgisini silmek istediğinize emin misiniz?"
                  onConfirm={() => deletePersonnelExam(item.id)}
                  okText="Evet"
                  cancelText="Hayır">
                  <Button icon={<DeleteOutlined />} size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            }>
            <Space direction="vertical">
              <Text>
                Tarih: {item.month}/{item.year}
              </Text>
              {item.description && <Text>Açıklama: {item.description}</Text>}
            </Space>
          </Card>
        ))
      )}
    </Card>
  );
};
